import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Plus,
  ArrowLeft,
  RotateCcw,
  Menu,
  Minus,
  Maximize2,
} from "lucide-react";
import { useChatStore } from "./chatStore";
import { useChatStream } from "./useChatStream";
import { ChatWelcome } from "./ChatWelcome";
import { ChatMessageList } from "./ChatMessageList";
import { ChatInput } from "./ChatInput";
import { ChatSidebar } from "./ChatSidebar";
import { createSession, getMessages, listSessions, renameSession } from "./chatApi";
import type { PendingMessage } from "./types";
import "./chat.css";

export const ChatDrawer: React.FC = () => {
  const isOpen = useChatStore((s) => s.isOpen);
  const activeSession = useChatStore((s) => s.activeSession);
  const messages = useChatStore((s) => s.messages);
  const error = useChatStore((s) => s.error);
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const confirmationRequest = useChatStore((s) => s.confirmationRequest);
  const confirmedTools = useChatStore((s) => s.confirmedTools);
  const closeChat = useChatStore((s) => s.closeChat);
  const minimize = useChatStore((s) => s.minimize);
  const toggleSidebar = useChatStore((s) => s.toggleSidebar);
  const setActiveSession = useChatStore((s) => s.setActiveSession);
  const setMessages = useChatStore((s) => s.setMessages);
  const setError = useChatStore((s) => s.setError);
  const { sendMessage } = useChatStream();

  // Inline title editing
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);

  const handleTitleClick = useCallback(() => {
    if (!activeSession) return;
    setTitleDraft(activeSession.title || "");
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  }, [activeSession]);

  const handleTitleSave = useCallback(async () => {
    if (activeSession && titleDraft.trim()) {
      try {
        await renameSession(activeSession.id, titleDraft.trim());
        listSessions()
          .then((data) => useChatStore.getState().setSessions(data.results))
          .catch(() => {});
      } catch { /* ignore */ }
    }
    setEditingTitle(false);
  }, [activeSession, titleDraft]);

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleTitleSave();
    if (e.key === "Escape") setEditingTitle(false);
  }, [handleTitleSave]);

  // Load messages when a session becomes active
  useEffect(() => {
    if (!activeSession) return;
    let cancelled = false;
    getMessages(activeSession.id)
      .then((data) => {
        if (cancelled) return;
        const pending: PendingMessage[] = data.results.map((m) => ({
          localId: `server-${m.id}`,
          role: m.role,
          content: m.content,
        }));
        setMessages(pending);
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : "Failed to load messages",
          );
      });
    return () => {
      cancelled = true;
    };
  }, [activeSession, setMessages, setError]);

  const handleSend = useCallback(
    async (message: string) => {
      let session = activeSession;
      if (!session) {
        try {
          session = await createSession(message.slice(0, 50));
          setActiveSession(session);
          // Refresh session list
          listSessions()
            .then((data) => useChatStore.getState().setSessions(data.results))
            .catch(() => {});
        } catch {
          setError("Failed to create chat session");
          return;
        }
      }
      sendMessage(session.id, message);
    },
    [activeSession, sendMessage, setActiveSession, setError],
  );

  const handleNewChat = useCallback(() => {
    setActiveSession(null);
  }, [setActiveSession]);

  const handleMaximize = useCallback(() => {
    window.open("/", "_blank");
  }, []);

  const lastUserMessage = useMemo(
    () => [...messages].reverse().find((m) => m.role === "user")?.content,
    [messages],
  );

  const handleRetry = useCallback(() => {
    if (!lastUserMessage || !activeSession) return;
    setError(null);
    const msgs = [...messages];
    while (
      msgs.length > 0 &&
      msgs[msgs.length - 1].role === "assistant" &&
      !msgs[msgs.length - 1].content
    ) {
      msgs.pop();
    }
    if (msgs.length > 0 && msgs[msgs.length - 1].role === "user") {
      msgs.pop();
    }
    useChatStore.getState().setMessages(msgs);
    sendMessage(activeSession.id, lastUserMessage);
  }, [lastUserMessage, activeSession, messages, sendMessage, setError]);

  const handleConfirm = useCallback(() => {
    if (!confirmationRequest || !activeSession) return;
    const toolName = confirmationRequest.tool_name;
    const newConfirmed = [...confirmedTools, toolName];
    useChatStore.getState().addConfirmedTool(toolName);
    useChatStore.getState().setConfirmationRequest(null);
    useChatStore.getState().setStreamingPhase("streaming");
    // Re-send the last user message with confirmed tools
    if (lastUserMessage) {
      sendMessage(activeSession.id, lastUserMessage, newConfirmed);
    }
  }, [confirmationRequest, activeSession, confirmedTools, lastUserMessage, sendMessage]);

  const handleReject = useCallback(() => {
    useChatStore.getState().setConfirmationRequest(null);
    useChatStore.getState().setStreamingPhase("idle");
    useChatStore.getState().clearToolState();
    // Append cancellation note to last assistant message
    const msgs = [...useChatStore.getState().messages];
    if (msgs.length > 0 && msgs[msgs.length - 1].role === "assistant") {
      msgs[msgs.length - 1] = {
        ...msgs[msgs.length - 1],
        content: msgs[msgs.length - 1].content + "\n\n*Action cancelled by user.*",
        streaming: false,
      };
      useChatStore.getState().setMessages(msgs);
    }
  }, []);

  return (
    <div className="chat-drawer" data-open={isOpen}>
      {/* Header */}
      <div className="chat-drawer-header">
        <div className="flex items-center gap-1.5">
          {activeSession ? (
            <button
              onClick={handleNewChat}
              className="p-1 rounded hover:bg-[var(--bg-muted)] transition-colors"
              aria-label="Back to new chat"
            >
              <ArrowLeft size={16} />
            </button>
          ) : (
            <button
              onClick={toggleSidebar}
              className="p-1 rounded hover:bg-[var(--bg-muted)] transition-colors"
              aria-label={sidebarOpen ? "Close sidebar" : "Open history"}
            >
              <Menu size={16} />
            </button>
          )}
          {editingTitle ? (
            <input
              ref={titleInputRef}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => handleTitleSave()}
              onKeyDown={handleTitleKeyDown}
              className="bg-transparent border-b text-sm font-semibold outline-none w-24"
              style={{ color: "var(--text-primary)", borderColor: "var(--border)" }}
            />
          ) : (
            <span
              onClick={handleTitleClick}
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--text-primary)",
                cursor: activeSession ? "pointer" : "default",
              }}
            >
              {activeSession ? activeSession.title || "Chat" : "AI Assistant"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {activeSession && (
            <button
              onClick={handleNewChat}
              className="p-1.5 rounded hover:bg-[var(--bg-muted)] transition-colors"
              aria-label="New chat"
            >
              <Plus size={14} />
            </button>
          )}
          <button
            onClick={minimize}
            className="p-1.5 rounded hover:bg-[var(--bg-muted)] transition-colors"
            aria-label="Minimize"
          >
            <Minus size={14} />
          </button>
          <button
            onClick={handleMaximize}
            className="p-1.5 rounded hover:bg-[var(--bg-muted)] transition-colors"
            aria-label="Open in full page"
          >
            <Maximize2 size={14} />
          </button>
          <button
            onClick={closeChat}
            className="p-1.5 rounded hover:bg-[var(--bg-muted)] transition-colors"
            aria-label="Close chat"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Body — sidebar overlays the chat area */}
      <div className="flex-1 overflow-hidden relative flex flex-col">
        <ChatSidebar />

        {!sidebarOpen && (
          <>
            {!activeSession && messages.length === 0 ? (
              <ChatWelcome onSelectPrompt={handleSend} />
            ) : (
              <ChatMessageList onConfirm={handleConfirm} onReject={handleReject} />
            )}
          </>
        )}
      </div>

      {/* Error */}
      {error && !sidebarOpen && (
        <div
          style={{
            padding: "8px 16px",
            fontSize: 13,
            color: "var(--danger)",
            background: "color-mix(in srgb, var(--danger) 8%, transparent)",
            borderTop: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <span>{error}</span>
          {lastUserMessage && activeSession && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1 shrink-0 text-xs px-2 py-1 rounded transition-colors"
              style={{
                color: "var(--danger)",
                border: "1px solid var(--danger)",
              }}
              aria-label="Retry last message"
            >
              <RotateCcw size={12} />
              Retry
            </button>
          )}
        </div>
      )}

      {/* Input */}
      {!sidebarOpen && <ChatInput onSend={handleSend} />}
    </div>
  );
};
